import React from 'react'
import { Form, Input, Modal, Select } from 'antd'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'

import { Trailer } from '@types'
import { useEquipment, useUserResponseManager } from '@hooks'
import { useUser } from '@contexts'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'

type Props = {
  open: boolean
  onClose: () => void
  onAdd: (data: Trailer) => void
}

const AddTrailerModal: React.FC<Props> = ({ open, onClose, onAdd }) => {
  const { t } = useTranslation()
  const { equipments } = useEquipment()
  const { state: user } = useUser()
  const { getUser } = useUserResponseManager(user?.id)
  const trailers = getUser?.user?.userDetail?.carrier?.trailers

  const validationSchema = yup.object().shape({
    licensePlate: yup
      .string()
      .required('Campo obligatorio')
      .min(5, 'La matrícula debe tener más de 5 caracteres')
      .max(10, 'La matrícula no debe tener mas de 10 caracteres')
      .matches(
        /^[A-Z0-9\s]*$/,
        'La matrícula debe contener caracteres alfanuméricos y ser todas las letras mayusculas',
      )
      .test('no-only-spaces', 'La matrícula no puede consistir solo en espacios', value => {
        if (value?.trim() === '') {
          throw new yup.ValidationError(
            'La matrícula no puede consistir solo en espacios',
            value,
            'licensePlate',
          )
        }
        return true
      })
      .test(
        'unique-state-id',
        'Ya existe un acoplado con la misma matrícula',
        value => !trailers?.some(trailer => trailer?.licensePlate === value),
      ),
    description: yup
      .string()
      .required('Campo obligatorio')
      .max(20, 'La descripcíon no debe tener mas de 20 caracteres')
      .matches(/^[a-zA-ZÀ-ÿ0-9\s]*$/, 'La descripcíon debe contener caracteres alfanuméricos')
      .test('no-only-spaces', 'La descripcíon no puede consistir solo en espacios', value => {
        if (value?.trim() === '') {
          throw new yup.ValidationError(
            'La descripcíon no puede consistir solo en espacios',
            value,
            'description',
          )
        }
        return true
      }),
    equipmentType: yup.object().required('Campo obligatorio'),
  })

  const { reset, handleSubmit, register, setValue, formState } = useForm<{
    licensePlate: string
    description: string
    equipmentType: { id: unknown }
  }>({
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = (data: Trailer) => {
    onAdd(data)
    reset()
  }

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        reset()
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.add_trailer_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.add_truck_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.add_truck_modal.button_cancel')}
      onOk={handleSubmit(
        onSubmit as unknown as SubmitHandler<{
          licensePlate: string
          description: string
          equipmentType: {
            id: unknown
          }
        }>,
        e => {
          console.log(e)
        },
      )}
      destroyOnClose
    >
      <StyledForm>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_truck_modal.licensePlate')}
            name="licensePlate"
            hasFeedback
            validateStatus={formState.errors.licensePlate ? 'error' : ''}
            help={formState.errors.licensePlate?.message}
          >
            <Input
              {...register('licensePlate')}
              onChange={e => {
                setValue('licensePlate', e.target.value)
              }}
              maxLength={10}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_truck_modal.description')}
            name="description"
            hasFeedback
            validateStatus={formState.errors.description ? 'error' : ''}
            help={formState.errors.description?.message}
          >
            <Input
              {...register('description')}
              onChange={e => {
                setValue('description', e.target.value)
              }}
              maxLength={20}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_truck_modal.equipmentType')}
            name="equipmentType"
            hasFeedback
            validateStatus={formState.errors.equipmentType ? 'error' : ''}
            help={formState.errors.equipmentType?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              options={equipments
                ?.filter(equipment => !equipment.straightTruck)
                .map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
              onChange={value => {
                setValue('equipmentType', { id: value })
              }}
            />
          </Form.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { AddTrailerModal }
